import React, { useState, useEffect, useRef } from 'react';
import { Bell, Mail, MailOpen, Clock, ChevronRight, X, Inbox } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { notificationService, UserNotification } from '../admin/services/adminAuth';
import { useAuth } from '../../auth/hooks/auth';

function NotificationSkeleton() {
  return (
    <div className="animate-pulse bg-white rounded-lg border border-gray-200 p-5">
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-lg bg-gray-100 flex-shrink-0" />
        <div className="flex-1 space-y-2">
          <div className="h-4 bg-gray-100 rounded w-2/3" />
          <div className="h-3 bg-gray-100 rounded w-full" />
          <div className="h-3 bg-gray-100 rounded w-1/2" />
        </div>
      </div>
    </div>
  );
}

interface NotificationDetailModalProps {
  notification: UserNotification;
  onClose: () => void;
}

/** Detects whether a string looks like an HTML document/snippet */
function isHtmlContent(str: string): boolean {
  if (!str) return false;
  const trimmed = str.trim();
  return (
    trimmed.startsWith('<!DOCTYPE') ||
    trimmed.startsWith('<html') ||
    /<(div|table|p|span|br|img|a|h[1-6])\b/i.test(trimmed)
  );
}

/** Renders HTML body inside a sandboxed iframe that auto-sizes to its content */
function HtmlEmailRenderer({ html }: { html: string }) {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) return;

    doc.open();
    doc.write(html);
    doc.close();

    // Auto-resize iframe to fit content height
    const resize = () => {
      try {
        const height = doc.documentElement.scrollHeight || doc.body.scrollHeight;
        iframe.style.height = height + 'px';
      } catch {
        // cross-origin safety fallback
      }
    };

    iframe.onload = resize;
    // Also attempt resize after a short delay for images/fonts
    const timer = setTimeout(resize, 300);
    return () => clearTimeout(timer);
  }, [html]);

  return (
    <iframe
      ref={iframeRef}
      title="notification-email"
      sandbox="allow-same-origin"
      style={{
        width: '100%',
        minHeight: '200px',
        border: 'none',
        display: 'block',
      }}
    />
  );
}

function NotificationDetailModal({ notification, onClose }: NotificationDetailModalProps) {
  const formatFullDate = (dateStr: string) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const body = notification.body || '';
  const renderAsHtml = isHtmlContent(body);

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-50" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
          {/* Modal Header */}
          <div className="flex items-start justify-between p-5 border-b border-gray-100">
            <div className="flex items-center gap-3 flex-1 min-w-0 pr-4">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                <MailOpen className="w-5 h-5 text-blue-600" />
              </div>
              <div className="min-w-0">
                <h3 className="font-semibold text-gray-900 text-base leading-snug">
                  {notification.subject || 'Notification'}
                </h3>
                <p className="text-xs text-gray-400 mt-0.5">
                  {formatFullDate(notification.dateSent || notification.dateCreated)}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg flex-shrink-0"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Modal Body */}
          <div className="flex-1 overflow-y-auto p-5">
            {renderAsHtml ? (
              // Render full HTML email visually
              <HtmlEmailRenderer html={body} />
            ) : (
              // Fallback: plain text
              <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap">
                {body || 'No message content.'}
              </p>
            )}
          </div>

          {/* Modal Footer */}
          <div className="px-5 py-4 border-t border-gray-100 bg-gray-50/50 rounded-b-xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs text-gray-400">
                {notification.messageRead ? (
                  <>
                    <MailOpen className="w-3.5 h-3.5" />
                    <span>
                      Read{' '}
                      {notification.dateRead
                        ? `on ${new Date(notification.dateRead).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })}`
                        : ''}
                    </span>
                  </>
                ) : (
                  <>
                    <Mail className="w-3.5 h-3.5" />
                    <span>Unread</span>
                  </>
                )}
              </div>
              <button
                onClick={onClose}
                className="px-4 py-1.5 bg-[#0F172A] text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function NotificationsPage() {
  const { user } = useAuth();
  const location = useLocation();
  const [notifications, setNotifications] = useState<UserNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedNotification, setSelectedNotification] = useState<UserNotification | null>(null);

  const openNotificationId = (location.state as any)?.openNotificationId;

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const res = await notificationService.getUserNotifications(false);
      if (res.isSuccess) setNotifications(res.data);
    } catch (err) {
      } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  // Auto-open notification from navigation state
  useEffect(() => {
    if (openNotificationId && notifications.length > 0) {
      const found = notifications.find(n => n.id === openNotificationId);
      if (found) {
        handleOpenNotification(found);
        window.history.replaceState({}, '');
      }
    }
  }, [openNotificationId, notifications]);

  const handleOpenNotification = async (notification: UserNotification) => {
    if (!notification.messageRead) {
      try {
        await notificationService.markMessageAsRead(notification.id);
        setNotifications(prev =>
          prev.map(n => n.id === notification.id ? { ...n, messageRead: true } : n)
        );
        setSelectedNotification({ ...notification, messageRead: true });
      } catch (err) {
        setSelectedNotification(notification);
      }
    } else {
      setSelectedNotification(notification);
    }
  };

  const truncate = (str: string, max: number) =>
    str && str.length > max ? str.slice(0, max) + '…' : str;

  const unreadCount = notifications.filter(n => !n.messageRead).length;

  return (
    <div className="w-full">
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Notifications</h1>
        <p className="text-gray-500 text-sm sm:text-base">
          {unreadCount > 0
            ? `You have ${unreadCount} unread notification${unreadCount !== 1 ? 's' : ''}.`
            : 'All notifications are up to date.'}
        </p>
      </div>

      {/* Stats Row */}
      {!loading && notifications.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg border border-gray-200 p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gray-50 flex items-center justify-center">
              <Bell className="w-4 h-4 text-gray-500" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{notifications.length}</div>
              <div className="text-xs text-gray-500">Total</div>
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
              <Mail className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{unreadCount}</div>
              <div className="text-xs text-gray-500">Unread</div>
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-green-50 flex items-center justify-center">
              <MailOpen className="w-4 h-4 text-green-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{notifications.length - unreadCount}</div>
              <div className="text-xs text-gray-500">Read</div>
            </div>
          </div>
        </div>
      )}

      {/* Notification List */}
      <div className="space-y-3">
        {loading ? (
          Array.from({ length: 5 }).map((_, i) => <NotificationSkeleton key={i} />)
        ) : notifications.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 flex flex-col items-center justify-center py-20 gap-4">
            <div className="w-16 h-16 rounded-full bg-gray-50 flex items-center justify-center">
              <Inbox className="w-8 h-8 text-gray-300" />
            </div>
            <div className="text-center">
              <p className="font-medium text-gray-600 text-base mb-1">No notifications yet</p>
              <p className="text-sm text-gray-400">
                You're all caught up! Notifications will appear here when something important happens.
              </p>
            </div>
          </div>
        ) : (
          notifications.map(notification => (
            <div
              key={notification.id}
              className={`bg-white rounded-lg border transition-all hover:shadow-sm ${
                !notification.messageRead ? 'border-blue-200 bg-blue-50/30' : 'border-gray-200'
              }`}
            >
              <div className="flex items-start gap-4 p-5">
                {/* Icon */}
                <div
                  className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    !notification.messageRead ? 'bg-blue-100' : 'bg-gray-100'
                  }`}
                >
                  {notification.messageRead ? (
                    <MailOpen className="w-5 h-5 text-gray-500" />
                  ) : (
                    <Mail className="w-5 h-5 text-blue-600" />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3 mb-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <h4
                        className={`text-sm font-semibold leading-snug truncate ${
                          !notification.messageRead ? 'text-gray-900' : 'text-gray-700'
                        }`}
                      >
                        {truncate(notification.subject || 'Notification', 60)}
                      </h4>
                      {!notification.messageRead && (
                        <span className="flex-shrink-0 px-2 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-semibold rounded-full uppercase tracking-wide">
                          New
                        </span>
                      )}
                    </div>

                    {/* Read / View Button */}
                    <button
                      onClick={() => handleOpenNotification(notification)}
                      className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300"
                    >
                      <span>{notification.messageRead ? 'View' : 'Read'}</span>
                      <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </div>

                  <p className="text-sm text-gray-500 mb-2 line-clamp-2 leading-relaxed pr-2">
                    <b>Sent to:</b> {notification.to}
                  </p>

                  <div className="flex items-center gap-1.5 text-xs text-gray-400">
                    <Clock className="w-3.5 h-3.5" />
                    <span>{formatDate(notification.dateSent || notification.dateCreated)}</span>
                    {notification.messageRead && notification.dateRead && (
                      <>
                        <span className="text-gray-200">•</span>
                        <MailOpen className="w-3.5 h-3.5 text-green-500" />
                        <span className="text-green-500">Read {formatDate(notification.dateRead)}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Detail Modal */}
      {selectedNotification && (
        <NotificationDetailModal
          notification={selectedNotification}
          onClose={() => setSelectedNotification(null)}
        />
      )}
    </div>
  );
}

export default NotificationsPage;